import type { ESTree } from 'meriyah'

export type JsLiteralValue =
    | string
    | number
    | boolean
    | null
    | undefined
    | JsLiteralValue[]
    | { [key: string]: JsLiteralValue }

const SUPPORTED_LITERAL_TYPES = ['Literal', 'ObjectExpression', 'ArrayExpression']

export const assertSupportedExpression = (node: ESTree.Expression | ESTree.SpreadElement): void => {
    if (node.type === 'SpreadElement') {
        throw new Error('SpreadElement is not supported in element definitions')
    }
    if (!SUPPORTED_LITERAL_TYPES.includes(node.type)) {
        throw new Error(
            `Unsupported expression type "${node.type}" in element definition. Only literal values are allowed.`,
        )
    }
}

export const astToValue = (node: ESTree.Expression | ESTree.SpreadElement): JsLiteralValue => {
    assertSupportedExpression(node)

    if (node.type === 'Literal') {
        if ('bigint' in node && node.bigint !== undefined) {
            throw new Error('BigInt literals are not supported in element definitions')
        }
        if ('regex' in node && node.regex !== undefined) {
            throw new Error('RegExp literals are not supported in element definitions')
        }
        return node.value as JsLiteralValue
    }
    if (node.type === 'ObjectExpression') {
        const result: { [key: string]: JsLiteralValue } = {}
        for (const prop of node.properties) {
            if (prop.type === 'SpreadElement') {
                throw new Error('SpreadElement is not supported in element definitions')
            }
            if (prop.type === 'Property' && prop.key.type === 'Identifier') {
                result[prop.key.name] = astToValue(prop.value as ESTree.Expression)
            } else if (prop.type === 'Property' && prop.key.type === 'Literal') {
                result[String(prop.key.value)] = astToValue(prop.value as ESTree.Expression)
            }
        }
        return result
    }
    if (node.type === 'ArrayExpression') {
        return node.elements.map(el => (el ? astToValue(el) : null))
    }
    return undefined
}
